/**
 * B709 leg 2 — the member portal, `/me/membership` (Figma 643:2 @390), on
 * B632's signed-in shell. Faces only: `lib/meMembership.ts` shapes the reads,
 * `useMeMembership.ts` makes them.
 * B703 (a): RENEW EARLY (`useMemberRenew.ts`) — the frame's UPGRADE and
 * "Change card ›" have no buyer door and stay out.
 * B795: the sidecar (spec/redline-storefront-member-portal.json) — measured
 * against the frame's numbers; ACTIVE the pill, the renewal card in its order.
 * B709 (a3): REDEEM YOUR GIFT on an unredeemed gift (`useGiftRedeem.ts`) —
 * no frame draws it; built per FORMS.md, the CTA in 645:2's words.
 */
import { useState, type ReactElement } from "react";
import { Banner, Button, EmptyState, Fieldset, Input, ProgressBar, Skeleton, StageChip, Stepper, Switch } from "@transact/ui";
import { giftRedeemWhy, type GiftDraft, type MembershipFace, type PortalRow } from "../lib/meMembership.js";
import { MeShell } from "./mePage.js";
import type { MemberRenewing } from "./useMemberRenew.js";

/** B709 (a3) — a gift being redeemed: the press in flight, or its answer in words. */
export interface GiftRedeeming {
  readonly memberNumber: string;
  readonly busy: boolean;
  readonly banner?: { readonly tone: "success" | "danger"; readonly text: string };
}

function GiftPanel(props: {
  readonly face: MembershipFace;
  readonly gift: NonNullable<MembershipFace["gift"]>;
  readonly onRedeem?: ((memberNumber: string, draft: GiftDraft) => void) | undefined;
  readonly redeeming?: GiftRedeeming | undefined;
}): ReactElement {
  const { face, gift } = props;
  const [d, setD] = useState<GiftDraft>({ adult1: "", adult2: "", children: 0 });
  const [pressed, setPressed] = useState(false);
  const why = giftRedeemWhy(d);
  const id = `me-m-${face.memberNumber}-gift`;
  const busy = props.redeeming?.busy === true;
  const press = (): void => {
    setPressed(true);
    if (why === undefined && !busy) props.onRedeem?.(face.memberNumber, d);
  };
  return (
    <section className="me-panel me-mpanel" aria-labelledby={id} data-redline="me-mgift">
      <h2 className="me-section-label" id={id}>
        Redeem your gift
      </h2>
      {props.onRedeem === undefined ? (
        <p className="me-mnote">Redeem it at any ticket window with the code in your gift email.</p>
      ) : (
        <form
          aria-label="redeem your gift"
          className="me-mgift-form"
          onSubmit={(e) => e.preventDefault()}
        >
          <p className="me-mnote">{`Your membership starts the day you redeem it and runs a full ${gift.termWord}.`}</p>
          <Input
            id={`${id}-adult1`}
            label={gift.adults >= 2 ? "First adult" : "Your name"}
            hint="Printed on the card; the gate may ask to see photo identification."
            value={d.adult1}
            onChange={(v) => setD({ ...d, adult1: v })}
            {...(pressed && why !== undefined ? { error: why } : {})}
          />
          {gift.adults >= 2 ? (
            <Input id={`${id}-adult2`} label="Second adult (optional)" hint="Gets their own card. Leave empty to add one later at a ticket window." value={d.adult2} onChange={(v) => setD({ ...d, adult2: v })} />
          ) : null}
          {gift.childrenMax > 0 ? (
            <Fieldset legend="Children" {...(gift.childrenHint !== undefined ? { hint: gift.childrenHint } : {})}>
              <Stepper label="Children" value={d.children} min={0} max={gift.childrenMax} onChange={(v) => setD({ ...d, children: v })} />
            </Fieldset>
          ) : null}
          <Button variant="primary" type="submit" busy={busy} onClick={press}>
            Redeem my gift
          </Button>
        </form>
      )}
      {props.redeeming?.memberNumber === face.memberNumber && props.redeeming.banner !== undefined ? <Banner tone={props.redeeming.banner.tone}>{props.redeeming.banner.text}</Banner> : null}
    </section>
  );
}

function Rows(props: { readonly rows: readonly PortalRow[] }): ReactElement {
  return (
    <dl className="me-mrows">
      {props.rows.map((r) => (
        <div className="me-mrow" key={r.label}>
          <dt>{r.label}</dt>
          <dd>{r.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function Membership(props: {
  readonly face: MembershipFace;
  readonly onRenew?: ((memberNumber: string) => void) | undefined;
  readonly renewing?: MemberRenewing | undefined;
  readonly onRedeemGift?: ((memberNumber: string, draft: GiftDraft) => void) | undefined;
  readonly redeeming?: GiftRedeeming | undefined;
}): ReactElement {
  const { face } = props;
  const renewing = props.renewing?.memberNumber === face.memberNumber ? props.renewing : undefined;
  const door = face.renewal?.renew;
  const id = `me-m-${face.memberNumber}`;
  return (
    <>
      <section className="me-mcard" aria-labelledby={`${id}-tier`} data-redline="me-mcard">
        <div className="me-mcard-head">
          <h2 className="me-mcard-tier" id={`${id}-tier`}>
            {face.tierName}
          </h2>
          <StageChip tone={face.chip.tone} shape="pill">
            {face.chip.word}
          </StageChip>
        </div>
        <p className="me-mcard-facts">{face.facts}</p>
        {face.cards.length > 0 ? (
          <ul className="me-mcard-cards">
            {face.cards.map((c) => (
              <li className="me-mcard-holder" key={c.cardId}>
                <span className="me-mcard-tile" aria-hidden="true">
                  {c.name.slice(0, 1)}
                </span>
                <p className="me-mcard-name">{c.name}</p>
                <p className="me-mcard-note">{c.note}</p>
              </li>
            ))}
          </ul>
        ) : null}
        <p className="me-mnote">{face.cards.length > 0 ? face.cardsNote : face.noCardsNote}</p>
      </section>
      {face.gift !== undefined ? <GiftPanel face={face} gift={face.gift} onRedeem={props.onRedeemGift} redeeming={props.redeeming} /> : null}
      {face.allowances.length > 0 ? (
        <section className="me-panel me-mpanel" aria-labelledby={`${id}-allow`} data-redline="me-mallowances">
          <h2 className="me-section-label" id={`${id}-allow`}>
            {face.allowancesLabel}
          </h2>
          {face.allowances.map((a) =>
            a.fraction !== undefined ? (
              <ProgressBar key={a.label} size="meter" label={a.label} fraction={a.fraction} detail={a.words} />
            ) : (
              <Rows key={a.label} rows={[{ label: a.label, value: a.words }]} />
            ),
          )}
          {face.allowancesNote !== undefined ? <p className="me-mnote">{face.allowancesNote}</p> : null}
        </section>
      ) : null}
      {face.renewal !== undefined ? (
        <section className="me-panel me-mpanel" aria-labelledby={`${id}-renew`} data-redline="me-mrenewal">
          <h2 className="me-section-label" id={`${id}-renew`}>
            Renewal
          </h2>
          <Switch
            id={`${id}-auto`}
            label="Renew automatically"
            checked={face.renewal.auto}
            /* dead-ok: shut with its reason (FORMS R15) — no buyer door turns auto-renew on or off yet */
            onChange={() => undefined}
            disabledWhy={face.renewal.shutWhy}
          />
          <Rows rows={face.renewal.rows} />
          {/* B795 — 643:2 reads rows → RENEW EARLY → what renewing does; the reminder sentence after */}
          {door !== undefined && props.onRenew !== undefined ? (
            <div className="me-mrenew" data-redline="me-mrenew">
              <Button variant="primary" busy={renewing?.busy === true} onClick={() => props.onRenew?.(face.memberNumber)}>
                {door.label}
              </Button>
              <p className="me-mnote">{door.note}</p>
            </div>
          ) : null}
          <p className="me-mnote">{face.renewal.note}</p>
          {renewing?.banner !== undefined ? <Banner tone={renewing.banner.tone}>{renewing.banner.text}</Banner> : null}
        </section>
      ) : null}
      <section className="me-panel me-mpanel" aria-labelledby={`${id}-house`} data-redline="me-mhousehold">
        <h2 className="me-section-label" id={`${id}-house`}>
          Household
        </h2>
        <Rows rows={face.household.rows} />
        <p className="me-mnote">{face.household.note}</p>
      </section>
    </>
  );
}

export function MeMembershipPage(props: {
  readonly email: string;
  readonly businessName?: string | undefined;
  readonly logoUrl?: string | undefined;
  /** "Priya" — the first named adult; absent = "Your membership". */
  readonly greeting?: string | undefined;
  /** undefined = still reading. */
  readonly faces: readonly MembershipFace[] | undefined;
  readonly error?: string | undefined;
  readonly ordersHref: string;
  /** The memberships on sale (`/memberships?tenant=`), for an email with none. */
  readonly joinHref: string;
  readonly onSignOut: () => void;
  readonly onRetry?: (() => void) | undefined;
  /** B703 (a) — the RENEW door; absent = no button (never a dead control). */
  readonly onRenew?: ((memberNumber: string) => void) | undefined;
  readonly renewing?: MemberRenewing | undefined;
  /** B709 (a3) — REDEEM MY GIFT; absent = the panel sends the buyer to a ticket window. */
  readonly onRedeemGift?: ((memberNumber: string, draft: GiftDraft) => void) | undefined;
  readonly redeeming?: GiftRedeeming | undefined;
}): ReactElement {
  const { faces } = props;
  return (
    <MeShell businessName={props.businessName} logoUrl={props.logoUrl} label="Your membership" here="My membership">
      <div className="me-who" data-redline="me-who">
        <p className="me-email" data-redline="me-email">{`Signed in as ${props.email}`}</p>
        {/* dead-ok: the href navigates — Button draws a real <a> for it, the press needs no handler */}
        <Button variant="textlink-accent" href={props.ordersHref} onClick={() => undefined}>
          My orders ›
        </Button>
      </div>
      <h1 className="me-title me-title--member" data-redline="me-title">
        {props.greeting !== undefined ? `Hi ${props.greeting}` : "Your membership"}
      </h1>
      {props.error !== undefined ? (
        <Banner tone="warn">
          {props.error}
          {props.onRetry !== undefined ? (
            <Button variant="textlink" onClick={props.onRetry}>
              Try again
            </Button>
          ) : null}
        </Banner>
      ) : null}
      {faces === undefined ? (
        props.error !== undefined ? null : (
          <div role="status" aria-label="Reading your membership">
            <Skeleton lines={8} />
          </div>
        )
      ) : faces.length === 0 ? (
        <EmptyState
          title={`No membership under ${props.email} yet`}
          body="Memberships bought with this email show here. Bought with another address? Sign out and use that one."
          action={<a href={props.joinHref}>See memberships</a>}
        />
      ) : (
        <div className="me-mstack">
          {faces.map((f) => (
            <Membership key={f.memberNumber} face={f} onRenew={props.onRenew} renewing={props.renewing} onRedeemGift={props.onRedeemGift} redeeming={props.redeeming} />
          ))}
        </div>
      )}
      <p className="me-foot" data-redline="me-foot">
        Only memberships bought with this email.{" "}
        <Button variant="textlink" onClick={props.onSignOut}>
          Sign out
        </Button>
      </p>
    </MeShell>
  );
}
